import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import {
  KyrgyzstanEmpty,
  KyrgyzstanLoader,
  hasCompleteKyrgyzstanBookingParams,
} from 'entities/kyrgyzstan'
import {
  clearSearchFormState,
  restoreSearchFormState,
} from 'entities/kyrgyzstan/lib'
import {
  kyrgyzstanDetailContextStore,
  kyrgyzstanDetailSelectionStore,
  kyrgyzstanSearchStore,
} from 'entities/kyrgyzstan/model'
import userStore from 'entities/user/module/user-store'

import { ButtonContinue } from 'shared/ui/bottom-continue'
import { ContactToWhatsapp } from 'shared/ui/contact-to-whatsapp'
import { HeaderGoBack } from 'shared/ui/header-go-back'

import s from './styles.module.scss'
import {
  AboutSection,
  AmenitiesSection,
  ExelyCancellationSection,
  ImageSection,
} from './ui'

const KyrgyzstanOptionDetailPage = observer(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = userStore.getAccountInfo()

  const { detailSearchParams, restoreSavedContext } =
    kyrgyzstanDetailContextStore
  const { selectedParent, selectedChild, restoreSelection } =
    kyrgyzstanDetailSelectionStore
  const [isSelectionRestored, setIsSelectionRestored] = useState(false)

  const { id } = useParams<{ id: string }>() as { id: string }

  const data = selectedChild?.id === id ? selectedChild : null
  const parentData =
    data && selectedParent?.id === data.parent_id ? selectedParent : null
  const canBook = hasCompleteKyrgyzstanBookingParams(detailSearchParams)

  useEffect(() => {
    restoreSavedContext()
    restoreSelection()
    setIsSelectionRestored(true)
  }, [restoreSavedContext, restoreSelection])

  useEffect(() => {
    if (user) {
      restoreSearchFormState(kyrgyzstanSearchStore)
      clearSearchFormState()
    }
  }, [user])

  const handleGoBack = () => {
    const canGoBack = window.history.state?.idx > 0

    if (canGoBack) {
      navigate(-1)
      return
    }

    if (parentData) {
      navigate(RoutingPaths.kyrgyzstan.detail.path(parentData.id))
      return
    }

    navigate(RoutingPaths.kyrgyzstan.path())
  }

  const handleSelectOption = async () => {
    if (!parentData || !data || !canBook) return

    navigate(RoutingPaths.kyrgyzstan.booking.path())
  }

  if (!isSelectionRestored) {
    return (
      <div className={s.container}>
        <HeaderGoBack
          title=""
          onGoBack={handleGoBack}
          variant="white"
          arrowColor="var(--inverse)"
          buttonWhite
        />
        <KyrgyzstanLoader />
      </div>
    )
  }

  if (!data || !parentData) {
    return (
      <div className={s.container}>
        <HeaderGoBack
          title=""
          onGoBack={handleGoBack}
          variant="white"
          arrowColor="var(--inverse)"
          buttonWhite
        />
        <KyrgyzstanEmpty title={t('kyrgyzstan_block.error.title')} />
      </div>
    )
  }

  return (
    <>
      <HeaderGoBack
        title={parentData?.title || ''}
        onGoBack={handleGoBack}
        variant="white"
        arrowColor="var(--inverse)"
        buttonWhite
      />

      <div className={s.container}>
        <ImageSection
          id={data.id}
          name={data.title}
          images={data.images}
        />

        <AboutSection description={data.description} />

        <AmenitiesSection
          popularDynamicFields={data.popular_dynamic_fields}
          dynamicFields={data.dynamic_fields}
          realtyId={data.id}
        />

        <ExelyCancellationSection
          cancellationPolicy={data.cancellation_policy}
        />

        <ContactToWhatsapp />
      </div>

      <ButtonContinue
        disabled={!canBook}
        onClick={handleSelectOption}
      >
        <span className="semi-bold-17">
          {t('kyrgyzstan_block.option_detail.select_room')}
        </span>
      </ButtonContinue>
    </>
  )
})

export default KyrgyzstanOptionDetailPage
